// DealActivityTimeline — how a deal was actually worked, as far as the CRM
// can tell us: stage progression + the CRM's own call gradings.
//
// HISTORY. This used to render HubSpot engagements (calls / notes / meetings /
// tasks) with an on-demand translation toggle. Sales moved to the partner CRM
// (crm.smartorange.space) and that CRM does not expose which activity belongs
// to which deal — its activity_links table is empty — so per-deal calls and
// notes are simply not obtainable today. Rather than render an empty box that
// looks like "the manager did nothing", we show what IS attributable and say
// plainly where the rest lives. When the CRM starts filling activity_links,
// /sales-crm/deals/{id}/activity grows the lists and this component follows.
//
// Marker: deal-activity-timeline-v2
import React, { useEffect, useState } from "react";
import { Badge } from "./ui/badge";
import { Activity, ArrowRight, ExternalLink, Loader2, Phone, TriangleAlert } from "lucide-react";
import api from "../lib/api";
import { crmUrl } from "../lib/crmUrl";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const scoreTone = (score) => {
  if (score === null || score === undefined) return "text-zinc-400";
  if (score >= 8) return "text-emerald-300";
  if (score >= 6) return "text-amber-300";
  return "text-red-300";
};

const DealActivityTimeline = ({ dealId, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/sales-crm/deals/${encodeURIComponent(dealId)}/activity`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load deal activity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  if (!dealId) return null;

  const pad = compact ? "px-2.5 py-2" : "px-3 py-2.5";
  const events = data?.stage_events || [];
  const scores = data?.call_scores || [];
  const acts = data?.activities || [];
  const owners = data?.owners || {};

  return (
    <div className="bg-zinc-950/40 border border-zinc-800 rounded">
      <div className={`${pad} flex items-center gap-2 border-b border-zinc-800`}>
        <Activity className="w-3.5 h-3.5 text-zinc-400" />
        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">
          How this deal was worked
        </span>
        {loading && <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />}
        <a
          href={crmUrl("deal", dealId)}
          target="_blank"
          rel="noreferrer"
          className="ml-auto text-[11px] text-zinc-400 hover:text-orange-400 inline-flex items-center gap-1"
        >
          Open in CRM <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {error && <div className={`${pad} text-[11px] text-red-300`}>{error}</div>}

      {!error && !loading && (
        <div className={`${pad} space-y-3`}>
          {scores.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Call gradings</div>
              {scores.map((s) => (
                <div key={s.id} className="flex items-start gap-2 text-[11px]">
                  <Phone className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-300">{formatDate(s.date)}</span>
                      <span className={`font-mono font-medium ${scoreTone(s.score)}`}>
                        {s.score ?? "—"}/10
                      </span>
                      {s.format && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 border-zinc-700 text-zinc-400">
                          {s.format}
                        </Badge>
                      )}
                      <span className="text-zinc-500">{owners[s.owner_id] || s.owner_id}</span>
                    </div>
                    {s.top_violation && (
                      <div className="text-zinc-400 leading-snug mt-0.5">{s.top_violation}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No transitions recorded (an imported or never-moved deal) still has
              a beginning and a current position — showing those beats showing
              an empty box under a heading that promises history. */}
          {events.length === 0 && data?.deal && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Stage</div>
              <div className="flex items-center gap-2 text-[11px] text-zinc-300">
                <span className="text-zinc-500 tabular-nums w-[86px] shrink-0">
                  {formatDate(data.deal.created_at)}
                </span>
                <span>created</span>
                {data.deal.stage_label && (
                  <>
                    <ArrowRight className="w-3 h-3 text-zinc-600 shrink-0" />
                    <span className="truncate">{data.deal.stage_label}</span>
                  </>
                )}
                {data.deal.loss_reason && (
                  <span className="text-zinc-500 truncate">· {data.deal.loss_reason}</span>
                )}
              </div>
            </div>
          )}

          {events.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Stage progression</div>
              {events.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-[11px] text-zinc-300">
                  <span className="text-zinc-500 tabular-nums w-[86px] shrink-0">{formatDate(e.entered_at)}</span>
                  {e.from_stage_label && (
                    <>
                      <span className="text-zinc-500 truncate">{e.from_stage_label}</span>
                      <ArrowRight className="w-3 h-3 text-zinc-600 shrink-0" />
                    </>
                  )}
                  <span className="truncate">{e.stage_label}</span>
                  {e.days_in_prev ? (
                    <span className="text-zinc-500 shrink-0">· {Math.round(e.days_in_prev)}d</span>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {acts.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Activity</div>
              {acts.map((a) => (
                <div key={a.id} className="flex items-start gap-2 text-[11px] text-zinc-300">
                  <span className="text-zinc-500 tabular-nums w-[86px] shrink-0">
                    {formatDate(a.occurred_at || a.created_at)}
                  </span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 border-zinc-700 text-zinc-400 shrink-0">
                    {a.type || "activity"}
                  </Badge>
                  {/* A note has no subject — its body IS the content. Show
                      whichever exists rather than the word "(no subject)". */}
                  <span className="truncate" title={a.subject || a.body || ""}>
                    {a.subject || a.body || "—"}
                  </span>
                  <span className="text-zinc-500 shrink-0">{owners[a.owner_id] || a.owner_id}</span>
                  {/* How we know this belongs to the deal. A rebuilt link is
                      not the CRM asserting it, and the row says so. */}
                  {a.deal_link_source && a.deal_link_source !== "link_table" && (
                    <span className="text-zinc-600 shrink-0" title={`Linked to this deal by ${a.deal_link_source}`}>
                      ~{a.deal_link_source}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Only shown when nothing could be tied to this deal — and it names
              the actual reason instead of a blanket "cannot be done". */}
          {data?.data_gap === "no_attributable_activity" && (
            <div className="flex items-start gap-2 text-[11px] text-zinc-500 border-t border-zinc-800/80 pt-2">
              <TriangleAlert className="w-3 h-3 mt-0.5 shrink-0 text-amber-500/70" />
              <span>
                No calls, e-mails or notes could be tied to this deal. The CRM keeps tasks and
                notes without any deal reference (its activity_links table is empty), so they are
                attributable only when a message address or a graded call points here. Empty does
                not mean nobody worked the deal.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DealActivityTimeline;
